import React from "react";
import { Box, Typography, MenuProps, useTheme } from "@mui/material";
import clsx from "clsx";
import {
  BodypartType,
  MovementPatternType,
} from "@growth-machine-llc/stridist-api-client";
import { AddedLozenge } from "../item/AddedLozenge";
import { BodypartTypeLabels, MovementPatternTypeLabels } from "./constants";
import { ExerciseCategoryMultiSelect } from "./ExerciseCategoryMultiSelect";
import { CategoryType, CategoryTypeLabel } from "./ManageLibraryExercise";
import { exerciseLibraryStyles } from "./exerciseLibraryStyles";

interface CategorySelectProps<T extends CategoryType> {
  label: CategoryTypeLabel;
  onChange?: (options: T[]) => void;
  onClickSettings?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  selectedOptions: T[];
  classes?: Partial<Record<"label" | "select" | "container", string>>;
  menuProps?: Partial<MenuProps>;
  disabled?: boolean;
}

const CategorySelect = <T extends CategoryType>(
  props: CategorySelectProps<T>,
) => {
  const { selectedOptions, label, classes, onChange, disabled } = props;
  const s = exerciseLibraryStyles();
  const theme = useTheme();

  const lozengeRef = React.useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const handleLozengeClick = () => {
    if (disabled) return;
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  return (
    <Box className={classes?.container}>
      <Box
        display={"flex"}
        flexDirection="row"
        justifyContent={"space-between"}
        mt={2}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          mb={1}
        >
          <Typography
            variant="h6"
            sx={{ m: 0 }}
            className={clsx(s.editorLabel, classes?.label)}
          >
            {label}
          </Typography>
        </Box>
        {!disabled && (
          <Box>
            <ExerciseCategoryMultiSelect<T>
              onClose={handleMenuClose}
              onChangeSelectedOptions={(selected: T[]) => {
                if (selected?.length <= 3 && onChange) {
                  onChange(selected);
                }
              }}
              initialSelected={selectedOptions}
              label={label}
              open={menuOpen}
              setOpen={setMenuOpen}
            />
          </Box>
        )}
      </Box>

      <Box gap={1} display="flex" flexWrap="wrap">
        {(!selectedOptions || selectedOptions.length === 0) &&
          (disabled ? (
            <Typography variant="body2" children="No options selected" />
          ) : (
            <Box ref={lozengeRef}>
              <AddedLozenge
                sx={{
                  "&.MuiBox-root": { m: 0 },
                  width: "fit-content",
                }}
                className={s.defaultOption}
                text={"Start by selecting a type"}
                onClick={handleLozengeClick}
              />
            </Box>
          ))}
        {selectedOptions &&
          selectedOptions.map((value) => (
            <AddedLozenge
              key={String(value)}
              sx={{ "&.MuiBox-root": { m: 0 }, width: "fit-content" }}
              text={
                label === CategoryTypeLabel.Bodypart
                  ? BodypartTypeLabels[value as BodypartType]
                  : MovementPatternTypeLabels[value as MovementPatternType]
              }
              onRemoveClick={() =>
                onChange(
                  selectedOptions.filter((option) => option !== value) as T[],
                )
              }
              disabled={disabled}
            />
          ))}
      </Box>
    </Box>
  );
};
export default CategorySelect;
